import { absoluteUrl, settingString, type SiteSettings } from "./site";

/**
 * The shape of `matches` a meta function receives, narrowed to what we read.
 *
 * Meta functions run for every route, and each route's generated `Route.MetaArgs`
 * types `matches` slightly differently. Only the root entry's loader data is
 * wanted here, so the parameter is typed structurally rather than imported from
 * any one route.
 */
export type MetaMatches =
  | readonly ({ id: string; loaderData?: unknown } | undefined)[]
  | undefined;

/**
 * Site settings, read out of the root loader's data from inside a meta function.
 *
 * Meta functions have no hooks, so `useSite()` is unavailable; `matches` is the
 * only route by which a leaf route can see what the root loaded. Every failure
 * mode returns empty settings, which every caller already treats as "fall back
 * to the hard-coded default".
 */
export function siteSettings(matches: MetaMatches): SiteSettings {
  const root = matches?.find((match) => match?.id === "root")?.loaderData as
    | { site?: { settings?: SiteSettings } }
    | undefined;
  return root?.site?.settings ?? {};
}

/**
 * The share card used by any page that has no image of its own.
 *
 * A built-in asset rather than only the CMS setting, because the setting is
 * optional and the consequence of leaving it unset is invisible from the admin:
 * the page shares as a bare text card and nobody finds out. The file is drawn
 * at 1200×630 — the ratio every platform crops to — which the brand logo is
 * not, so pointing this at the logo produced a share image cropped to a
 * horizontal band through the middle of it.
 *
 * A setting still wins where one is configured; this is the floor, not a
 * ceiling.
 */
export const FALLBACK_OG_IMAGE = "/og-default.png";

export function defaultOgImage(matches: MetaMatches): string {
  return (
    settingString(siteSettings(matches), "seo.defaultOgImageUrl") ||
    absoluteUrl(FALLBACK_OG_IMAGE)
  );
}

export interface SeoInput {
  /** Passed straight through from the meta function, for site-wide defaults. */
  matches?: MetaMatches;
  title: string;
  description?: string | null;
  /** Site-relative path, used for `og:url` and the canonical link. */
  path: string;
  /** Overrides `path`, for a page that is a duplicate of another. */
  canonical?: string | null;
  image?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  type?: "website" | "article";
  noIndex?: boolean;
  /** `noindex, follow` for paginated pages, which should not rank but should be crawled. */
  noIndexFollow?: boolean;
}

/**
 * One page's meta descriptors.
 *
 * Every public route emits the same set through here rather than each writing
 * out the half-dozen tags it happens to remember. That is what the routes were
 * doing, and the omissions were invisible from the source — three of them had
 * no canonical link and no `og:url` at all, so a share of `/contact` carried no
 * address and search engines were free to pick their own canonical from any
 * query-string variant they had crawled.
 *
 * Site-wide tags — `og:site_name`, `og:locale`, organisation markup — are
 * deliberately not here. React Router replaces a parent route's meta with the
 * child's rather than merging them, so anything emitted from the root's `meta`
 * is discarded the moment a route defines its own. Those tags live in the
 * document head in root.tsx instead, where nothing can drop them.
 */
export function seoMeta(input: SeoInput) {
  const canonical = input.canonical ?? absoluteUrl(input.path);
  const description = input.description?.trim() || "";
  const image = input.image ?? defaultOgImage(input.matches);
  const ogTitle = input.ogTitle ?? input.title;
  const ogDescription = input.ogDescription ?? description;

  return [
    { title: input.title },
    ...(description ? [{ name: "description", content: description }] : []),
    ...(input.noIndex
      ? [{ name: "robots", content: "noindex, nofollow" }]
      : input.noIndexFollow
        ? [{ name: "robots", content: "noindex, follow" }]
        : []),

    { property: "og:type", content: input.type ?? "website" },
    { property: "og:title", content: ogTitle },
    { property: "og:url", content: canonical },
    ...(ogDescription ? [{ property: "og:description", content: ogDescription }] : []),
    ...(image ? [{ property: "og:image", content: image }] : []),

    // `summary_large_image` only where there is an image to be large. Claiming
    // it without one renders as an empty card rather than a small one.
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: ogTitle },
    ...(ogDescription ? [{ name: "twitter:description", content: ogDescription }] : []),
    ...(image ? [{ name: "twitter:image", content: image }] : []),

    { tagName: "link", rel: "canonical", href: canonical },
  ];
}
